/**
 * The two playtest art surfaces, both walking `lib/logic.ts#ptArtCands` through
 * `useArt`.
 *
 * They exist as components rather than as two more `hasArt` / `artBg` fields on
 * the view-models because resolving a ladder is an `Image()` probe, and that is
 * a component concern (the argument is in `CoverArt.tsx`'s header and
 * PORTING-NOTES P2-01). The playtests screens render their cards inside a
 * `.map()`, so the hook cannot live in the screen either — a component per card
 * is what makes the call legal.
 *
 *   PtCover     the 460×N card thumbnail — Cover Art's checker + initials while
 *               the walk runs, the art on top once a rung loads.
 *   PtHeroArt   the detail / confirm hero, which paints its own initials rather
 *               than Cover Art's checker. Same ladder, same probe.
 *
 * `key` is `'pt' + <playtest id> + 'w'`. The `'pt'` prefix keeps a playtest off
 * the record a `GameId` of the same digits would own (`useArt` keys on
 * `g.id + kind`, and `GameId` is `number | string`), and the ids are uuids in
 * production, so the two namespaces cannot meet.
 */

import type { CSSProperties } from 'react';

import type { ArtCand } from '../lib/types.ts';
import { CoverArt } from './CoverArt.tsx';
import { useArt } from './useArt.ts';

const artKey = (id: string) => 'pt' + id + 'w';

/**
 * All four fields are required, not optional, because `exactOptionalPropertyTypes`
 * makes an explicitly-`undefined` prop a different thing from an absent one, and
 * every call site passes all four anyway.
 */
export interface PtCoverProps {
  /** the playtest id — or any stable id for a row that has no playtest yet */
  id: string;
  cands: readonly ArtCand[];
  /** initials px, per call site */
  fs: number | string;
  ini: string;
  /** host-wrapper style — positional properties only, as `CoverArt` requires */
  style: CSSProperties;
}

export function PtCover({ id, cands, fs, ini, style }: PtCoverProps): JSX.Element {
  const art = useArt(artKey(id), cands);
  return <CoverArt style={style} fs={fs} ini={ini} hasCover={art.hasCover} cover={art.cover} />;
}

export interface PtHeroArtProps {
  id: string;
  cands: readonly ArtCand[];
  ini: string;
  /** initials px — 20 on the phone sheets, 22 in the desktop modal */
  fs: number;
}

/**
 * The initials sit UNDER the art and are dropped once a rung resolves, which is
 * the state the three hero call sites used to derive from `showIni: !hasArt`.
 * The difference is that "no art" is now the probe's answer rather than a guess
 * about a URL string: a hero whose whole ladder 404s shows the initials, where
 * before it showed nothing at all.
 */
export function PtHeroArt({ id, cands, ini, fs }: PtHeroArtProps): JSX.Element {
  const art = useArt(artKey(id), cands);
  if (art.hasCover) {
    return <div style={{ position: 'absolute', inset: 0, background: art.cover }} />;
  }
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--gt-font-pixel)',
        fontSize: fs,
        color: 'rgba(255,255,255,.22)',
      }}
    >
      {ini}
    </div>
  );
}
